import { useMemo } from 'react';
import { StanFilterConfig } from '@/components/core-components/StanFilters/types';
import { buildProducts, ProductFilterOption } from '@/lib';
import { ORDER_FILTERS } from '@/lib/constants';
import { useUser } from '@/queries';
import { useOrders } from './useOrders';
import { OrderParamsRequest } from '@/lib/api/income/types';
import IcMoney from '@/assets/icons/ic-billing.svg';
import { useDefaultStore } from '@/stores/defaultStore';
import { useStoreStore } from '@/stores';

export function useOrderFilters(userFanId?: number): StanFilterConfig[] {
    const { user } = useUser();
    const store = useDefaultStore(state => state.store);
    const getFunnels = useStoreStore(state => state.getFunnels);
    const funnels = useMemo(() => getFunnels(), [getFunnels]);

    const disputePayload: OrderParamsRequest = {
        page: 1,
        per_page: 1,
        filter_array: JSON.stringify([
            {
                fieldName: 'orders-status',
                value: 'DISPUTED',
            },
        ]),
    };
    const { data: orders } = useOrders(disputePayload);
    const hasDispute = orders?.total && orders?.total > 0;

    return useMemo(() => {
        if (!store) return [];
        const productList = (
            buildProducts({
                store,
                funnels: funnels || [],
                format: 'options',
            }) as ProductFilterOption[]
        ).map(p => ({
            label: p.label,
            id: p.id,
            icon: p.icon,
        }));

        const filters: StanFilterConfig[] = [
            {
                id: 'orders-date-time',
                label: 'Date & Time',
                type: 'date',
            },
            ...(userFanId
                ? []
                : [
                      {
                          id: 'orders-eid',
                          label: 'Email',
                          type: 'text',
                      } as StanFilterConfig,
                  ]),
            {
                id: 'orders-product-id',
                label: 'Product',
                type: 'select',
                customDropdownReduce: 'id',
                customDropdownOptions: [
                    {
                        label: 'Cash Out',
                        id: ORDER_FILTERS.CASH_OUT_ID,
                        icon: IcMoney,
                    },
                    ...productList,
                ],
            },
            { id: 'orders-amount', label: 'Amount', type: 'number' },
            { id: 'orders-discount-code', label: 'Discount Code', type: 'text' },
            {
                id: 'orders-payment-processor',
                label: 'Payment Method',
                type: 'select',
                customDropdownReduce: 'type',
                customDropdownOptions: [
                    { label: 'Stripe', type: 'stripe' },
                    { label: 'Paypal', type: 'paypal' },
                    { label: 'Access Granted', type: 'manual-access-granted' },
                ],
            },
            {
                id: 'orders-status',
                label: 'Status',
                type: 'select',
                customDropdownReduce: 'type',
                customDropdownOptions: [
                    { label: 'Paid', type: 'PAID' },
                    { label: 'Refunded', type: 'REFUNDED' },
                    { label: 'Disputed', type: 'DISPUTED' },
                    { label: 'Failed / Retrying', type: 'FAILED' },
                ],
            },
        ];
        // Only show if has dispute
        if (hasDispute) {
            filters.push({
                id: 'orders-dispute-status',
                label: 'Dispute Status',
                type: 'select',
                customDropdownReduce: 'type',
                customDropdownOptions: [
                    { label: 'Inquiry', type: 'INQUIRY' },
                    { label: 'Won', type: 'DISPUTED WON' },
                    { label: 'Lost', type: 'DISPUTED LOST' },
                    { label: 'Needs Response', type: 'DISPUTED NEEDS RESPONSE' },
                ],
            });
        }

        if (user?.has_affiliate_transaction) {
            filters.push({
                id: 'orders-affiliate',
                label: 'Affiliate',
                type: 'select',
                customDropdownReduce: 'type',
                customDropdownOptions: [
                    { label: 'via Affiliate', type: 'via-affiliate' },
                    { label: 'Affiliate Commission', type: 'affiliate-fee' },
                ],
            });
        }

        return filters;
    }, [store, funnels, hasDispute, user?.has_affiliate_transaction]);
}
